import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { api, qs } from "../api/client";
import { errorMessage, keys } from "../api/hooks";
import { rulesResponseSchema, type LocationSummary, type Mode } from "../../api/contracts";
import { ruleScopeLabel } from "../lib/locations";
import { describeRuleSet, windowPhrase } from "../lib/rules";
import { Button, Dialog, Spinner } from "./ui";
import { UntouchedNote } from "./UntouchedNote";

/**
 * Turning a location on confirms at once: the dialog shows the rules that apply and points at
 * the Preview page for the numbers, instead of running a preview job of its own and making the
 * owner wait for it (they have usually run one already). The first real pass runs in the
 * background once the location is on.
 */
export function EnableDialog({ location, mode, busy, rulesHref, previewHref, onConfirm, onClose }: { location: LocationSummary; mode: Mode; busy: boolean; rulesHref: string; previewHref: string; onConfirm: () => void; onClose: () => void }) {
  const rules = useQuery({ queryKey: keys.rules("location", location.blvdId), queryFn: () => api(`/api/rules${qs({ scope: "location", key: location.blvdId })}`, rulesResponseSchema) });
  const effective = rules.data?.effective;

  return (
    <Dialog open title={`Turn on BTB blocks for ${location.name}?`} onClose={onClose}
      footer={<>
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={onConfirm} disabled={busy}>Turn on BTB blocks</Button>
      </>}>
      <p>BTB blocks will be planned for {windowPhrase(location.lookaheadDays)} and kept up to date as bookings change, using the rules that apply to this location.</p>
      <div className="rounded-md border border-hs-border p-3 text-sm">
        <p className="font-semibold text-hs-text">Rules for this location{effective && <>: {ruleScopeLabel(effective.scope)}</>}</p>
        {rules.isPending && <Spinner label="Loading rules…" />}
        {rules.isError && <p className="text-hs-text-muted">The rules could not be loaded ({errorMessage(rules.error)}).</p>}
        {effective && <ul className="mt-1 list-disc pl-5 text-hs-text-muted">{describeRuleSet(effective.rules).map((line) => <li key={line}>{line}</li>)}</ul>}
        <Link to={rulesHref} className="mt-2 inline-block font-semibold text-hs-deep underline">Change rules first</Link>
      </div>
      <UntouchedNote />
      <p className="text-sm text-hs-text-muted">Not sure what this will do? <Link to={previewHref} className="font-semibold text-hs-deep underline">Preview {windowPhrase(location.lookaheadDays)} first</Link>.</p>
      {mode === "shadow" && <p className="text-sm text-hs-text-muted">Preview mode is on for the whole system: nothing is written to Boulevard until corporate switches to live.</p>}
    </Dialog>
  );
}
